import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { Link } from 'react-router';

import { cn } from '@/shared/utils/cn';

/**
 * The phone version of a table: one card per row.
 *
 * A listing with six columns does not fit in 390 px; a table that scrolls sideways is a
 * table nobody reads on a phone. Under `md` a screen renders the same rows as `CardList`
 * items (title, a quiet line under it, badges on the right, whatever else below) and
 * keeps the `Table` for `md` and up. Both are in the DOM: it is CSS (`md:hidden` /
 * `hidden md:block`) that picks one, so the data is loaded once.
 *
 * The whole card is the link when given `to` (an `<a>`, so the middle click and the
 * context menu work) or the button when given `onClick` (with the keyboard: Enter and
 * Space). Buttons inside a card keep working on their own, same rule as `Tr`.
 */
export function CardList({
  label,
  className,
  children,
}: {
  /** What the list is, for screen readers. */
  label: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <ul aria-label={label} className={cn('flex flex-col gap-2', className)}>
      {children}
    </ul>
  );
}

interface CardListItemProps {
  /** The one thing the row is about: the subject, the number, the name. */
  title: ReactNode;
  /** The quiet line under the title: who, when, where from. */
  subtitle?: ReactNode;
  /** Badges or a short value, to the right of the title. */
  aside?: ReactNode;
  /** The bottom line: a date on the left, a destination on the right, whatever fits. */
  footer?: ReactNode;
  /** Makes the card a link to this route. */
  to?: string;
  /** Makes the card a button. Ignored when `to` is given. */
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  className?: string;
  children?: ReactNode;
}

export function CardListItem({
  title,
  subtitle,
  aside,
  footer,
  to,
  onClick,
  className,
  children,
}: CardListItemProps) {
  const surface = cn(
    'bg-card border-line rounded-card shadow-card block border px-4 py-3 text-left',
    (to || onClick) &&
      'hover:bg-paper-2/60 focus-visible:outline-accent cursor-pointer transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
  );

  const body = (
    <>
      <div className="flex items-start justify-between gap-3">
        <p className="text-ink min-w-0 flex-1 text-[14.5px] leading-5 font-medium">{title}</p>
        {aside ? <div className="flex shrink-0 flex-wrap justify-end gap-1">{aside}</div> : null}
      </div>
      {subtitle ? (
        <p className="text-ink-3 mt-0.5 truncate text-[13px] leading-5">{subtitle}</p>
      ) : null}
      {children ? <div className="text-ink-2 mt-2 text-[13px] leading-5">{children}</div> : null}
      {footer ? (
        <div className="text-ink-3 mt-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-[12.5px] leading-4">
          {footer}
        </div>
      ) : null}
    </>
  );

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.currentTarget.click();
    }
  }

  return (
    <li className={className}>
      {to ? (
        <Link to={to} className={surface}>
          {body}
        </Link>
      ) : onClick ? (
        <div
          role="button"
          tabIndex={0}
          onClick={onClick}
          onKeyDown={handleKeyDown}
          className={surface}
        >
          {body}
        </div>
      ) : (
        <div className={surface}>{body}</div>
      )}
    </li>
  );
}
